import { trpc } from '@/app/api/trpc/client';
import { useActivityStore } from '@/app/stores/activityStore';
import { useAnalysisStore } from '@/app/stores/analysisStore';
import { useTimelyAnalysisStore } from '@/app/stores/timelyAnalysisStore';
import { useWorkoutStore } from '@/app/stores/workoutStore';
import { ActivityWithRelations, WorkoutWithRelations } from '@/utils/types';
import type { Analysis, TimelyAnalysis } from '@trainme/db/client';
import { endOfDay, startOfDay } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DateRange {
  start: Date;
  end: Date;
}

// Helper to create a range key for tracking loaded ranges
const getRangeKey = (range: DateRange): string => {
  return `${range.start.getTime()}-${range.end.getTime()}`;
};

// Helper to check if a range is already covered by loaded ranges
const isRangeLoaded = (
  range: DateRange,
  loadedRanges: Set<string>
): boolean => {
  // Check if any loaded range covers this range
  for (const loadedKey of loadedRanges) {
    const [startTime, endTime] = loadedKey.split('-').map(Number);
    const loadedStart = new Date(startTime);
    const loadedEnd = new Date(endTime);

    // Check if the requested range is fully within a loaded range
    if (range.start >= loadedStart && range.end <= loadedEnd) {
      return true;
    }
  }
  return false;
};

export function useCalendar() {
  const {
    mergeActivities,
    getFilteredActivities,
    lastSyncAt: activitySyncAt,
  } = useActivityStore();
  const {
    mergeWorkouts,
    getFilteredWorkouts,
    lastSyncAt: workoutSyncAt,
  } = useWorkoutStore();
  const {
    mergeAnalyses,
    getFilteredAnalyses,
    lastSyncAt: analysisSyncAt,
  } = useAnalysisStore();
  const {
    mergeTimelyAnalyses,
    getFilteredTimelyAnalyses,
    lastSyncAt: timelyAnalysisSyncAt,
  } = useTimelyAnalysisStore();
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [isLoadingWorkouts, setIsLoadingWorkouts] = useState(false);

  // Track which date ranges have been loaded to avoid refetching
  const loadedRangesRef = useRef<Set<string>>(new Set());

  // Watch for global sync pulse to clear loaded ranges
  useEffect(() => {
    if (
      workoutSyncAt > 0 ||
      activitySyncAt > 0 ||
      analysisSyncAt > 0 ||
      timelyAnalysisSyncAt > 0
    ) {
      loadedRangesRef.current.clear();
    }
  }, [workoutSyncAt, activitySyncAt, analysisSyncAt, timelyAnalysisSyncAt]);

  const utils = trpc.useUtils();

  // Function to merge new data into our stores
  const mergeData = useCallback(
    (
      newActivities: ActivityWithRelations[],
      newWorkouts: WorkoutWithRelations[],
      newAnalyses: Analysis[],
      newTimelyAnalyses: TimelyAnalysis[]
    ) => {
      // Merge data into respective stores
      mergeActivities(newActivities);
      mergeWorkouts(newWorkouts);
      mergeAnalyses(newAnalyses);
      mergeTimelyAnalyses(newTimelyAnalyses);
    },
    [mergeActivities, mergeWorkouts, mergeAnalyses, mergeTimelyAnalyses]
  );

  // Query for a specific week's data
  const loadWeekData = useCallback(
    async (weekRange: DateRange, skipLoading = false) => {
      const rangeKey = getRangeKey(weekRange);

      // Check if this range is already loaded
      const isAlreadyLoaded = isRangeLoaded(weekRange, loadedRangesRef.current);

      // Check if we have data in the store for this range
      const existingActivities = getFilteredActivities(weekRange);
      const existingWorkouts = getFilteredWorkouts(weekRange);
      const existingAnalyses = getFilteredAnalyses(weekRange);
      const existingTimelyAnalyses = getFilteredTimelyAnalyses(weekRange);

      // If range is already loaded and we have data, return existing data without fetching
      if (
        isAlreadyLoaded &&
        (existingActivities.length > 0 ||
          existingWorkouts.length > 0 ||
          existingAnalyses.length > 0 ||
          existingTimelyAnalyses.length > 0)
      ) {
        return {
          activities: existingActivities,
          workouts: existingWorkouts,
          analyses: existingAnalyses,
          timelyAnalyses: existingTimelyAnalyses,
        };
      }

      // Only show loading indicators if we're actually fetching new data
      const shouldShowLoading = !skipLoading && !isAlreadyLoaded;

      if (shouldShowLoading) {
        setIsLoadingActivities(true);
        setIsLoadingWorkouts(true);
      }

      try {
        const [activities, workouts, analyses, timelyAnalyses] =
          await Promise.all([
            utils.activities.getMany
              .fetch({
                filter: {
                  startDate: {
                    gte: startOfDay(weekRange.start),
                    lt: endOfDay(weekRange.end),
                  },
                },
              })
              .finally(() => {
                if (shouldShowLoading) setIsLoadingActivities(false);
              }),
            utils.workouts.getMany
              .fetch({
                filter: {
                  date: {
                    gte: startOfDay(weekRange.start),
                    lt: endOfDay(weekRange.end),
                  },
                },
              })
              .finally(() => {
                if (shouldShowLoading) setIsLoadingWorkouts(false);
              }),
            utils.analysis.getMany.fetch({
              filter: {
                date: {
                  gte: startOfDay(weekRange.start),
                  lt: endOfDay(weekRange.end),
                },
              },
            }),
            utils.analysis.getTimelyAnalysisRange.fetch({
              startDate: startOfDay(weekRange.start),
              endDate: endOfDay(weekRange.end),
            }),
          ]);

        const safeAnalyses = analyses?.analyses ?? [];

        mergeData(
          activities.activities as ActivityWithRelations[],
          workouts.workouts as WorkoutWithRelations[],
          safeAnalyses as Analysis[],
          timelyAnalyses as TimelyAnalysis[]
        );

        // Mark this range as loaded
        loadedRangesRef.current.add(rangeKey);

        return {
          activities: activities.activities as ActivityWithRelations[],
          workouts: workouts.workouts as WorkoutWithRelations[],
          analyses: safeAnalyses as Analysis[],
          timelyAnalyses: timelyAnalyses as TimelyAnalysis[],
        };
      } catch (error) {
        if (shouldShowLoading) {
          setIsLoadingActivities(false);
          setIsLoadingWorkouts(false);
        }
        throw error;
      }
    },
    [
      mergeData,
      utils,
      getFilteredActivities,
      getFilteredWorkouts,
      getFilteredAnalyses,
      getFilteredTimelyAnalyses,
    ]
  );

  const refreshActivitiesForRange = useCallback(
    async (range: DateRange) => {
      const activities = await utils.activities.getMany.fetch({
        filter: {
          startDate: {
            gte: startOfDay(range.start),
            lt: endOfDay(range.end),
          },
        },
      });

      const mergedActivities =
        activities?.activities?.map((a) => a as ActivityWithRelations) ?? [];

      mergeActivities(mergedActivities);
    },
    [utils, mergeActivities]
  );

  const refreshWorkoutsForRange = useCallback(
    async (range: DateRange) => {
      const workouts = await utils.workouts.getMany.fetch({
        filter: {
          date: {
            gte: startOfDay(range.start),
            lt: endOfDay(range.end),
          },
        },
      });

      const mergedWorkouts =
        workouts?.workouts?.map((w) => w as WorkoutWithRelations) ?? [];

      mergeWorkouts(mergedWorkouts);
    },
    [utils, mergeWorkouts]
  );

  // Filter current stores by date range
  const getFilteredData = useCallback(
    (range?: DateRange) => {
      if (!range) {
        return {
          activities: getFilteredActivities(),
          workouts: getFilteredWorkouts(),
          analyses: getFilteredAnalyses(),
          timelyAnalyses: getFilteredTimelyAnalyses(),
        };
      }

      return {
        activities: getFilteredActivities(range),
        workouts: getFilteredWorkouts(range),
        analyses: getFilteredAnalyses(range),
        timelyAnalyses: getFilteredTimelyAnalyses(range),
      };
    },
    [
      getFilteredActivities,
      getFilteredWorkouts,
      getFilteredAnalyses,
      getFilteredTimelyAnalyses,
    ]
  );

  const { activities, workouts, analyses, timelyAnalyses } = getFilteredData();

  return {
    activities,
    workouts,
    analyses,
    timelyAnalyses,
    loadWeekData,
    isLoadingActivities,
    isLoadingWorkouts,
    lastWorkoutSyncAt: workoutSyncAt,
    lastActivitySyncAt: activitySyncAt,
    lastAnalysisSyncAt: analysisSyncAt,
    lastTimelyAnalysisSyncAt: timelyAnalysisSyncAt,
    refreshActivitiesForRange,
    refreshWorkoutsForRange,
  };
}
